import React, { useState } from "react";
import { consumeSignOutReason } from "../../services/sessionTimeout";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, Card, CardBody, FormGroup, Form, Input, InputGroupAddon,
  InputGroupText, InputGroup, Col, Alert, Spinner } from
"reactstrap";
import { useAuth } from "../../contexts/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, resetPassword } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [notice] = useState(() => {
    const reason = consumeSignOutReason();
    return { timeout: "session.timedOut", disabled: "session.disabled", session_limit: "session.sessionLimit", revoked: "session.revoked" }[reason] || null;
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      const code = err.code || "";
      if (code.includes("user-not-found")) setError(t("auth.errors.userNotFound"));else
      if (code.includes("wrong-password") || code.includes("invalid-credential")) setError(t("auth.errors.invalidCredential"));else
      if (code.includes("too-many-requests")) setError(t("auth.errors.tooManyRequests"));else
      if (code.includes("user-disabled")) {consumeSignOutReason();setError(t("session.disabled"));} else
      setError(t("auth.errors.generic"));
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    setMessage("");
    if (!email) {setError(t("auth.login.emailRequiredMsg"));return;}
    try {
      await resetPassword(email);
      setError("");
      setMessage(t("auth.login.emailSentMsg"));
    } catch {setError(t("auth.errors.generic"));}
  };

  return (
    <Col lg="5" md="7">
      <Card className="border-0">
        <CardBody className="p-4 p-md-5 fd-auth-form">
          <h2 className="fd-auth-form__title">{t("auth.login.title")}</h2>
          <p className="fd-auth-form__subtitle">{t("auth.login.subtitle")}</p>

          {notice && !error && <Alert color="warning" role="status" data-testid="signout-notice">{t(notice)}</Alert>}
          {error && <Alert color="danger" role="alert">{error}</Alert>}
          {message && <Alert color="success" role="status">{message}</Alert>}

          <Form role="form" onSubmit={handleLogin} noValidate>
            <FormGroup>
              <label htmlFor="login-email">{t("authShell.email")}</label>
              <InputGroup className="input-group-alternative">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText><i className="ni ni-email-83" /></InputGroupText>
                </InputGroupAddon>
                <Input id="login-email" placeholder={t("auth.login.emailPlaceholder")} type="email"
                autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
              </InputGroup>
            </FormGroup>
            <FormGroup>
              <label htmlFor="login-password">{t("authShell.password")}</label>
              <InputGroup className="input-group-alternative">
                <InputGroupAddon addonType="prepend">
                  <InputGroupText><i className="ni ni-lock-circle-open" /></InputGroupText>
                </InputGroupAddon>
                <Input id="login-password" placeholder={t("auth.login.passwordPlaceholder")}
                type={showPassword ? "text" : "password"} required
                autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <InputGroupAddon addonType="append">
                  <button type="button" className="fd-auth-form__pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("authShell.hidePassword") : t("authShell.showPassword")}
                  title={showPassword ? t("authShell.hidePassword") : t("authShell.showPassword")}>
                    <i className={showPassword ? "far fa-eye-slash" : "far fa-eye"} />
                  </button>
                </InputGroupAddon>
              </InputGroup>
            </FormGroup>
            <div className="fd-auth-form__row">
              <Button color="link" size="sm" className="p-0" type="button" onClick={handleForgotPassword}>
                {t("auth.login.forgotPassword")}
              </Button>
            </div>
            <Button color="primary" type="submit" block disabled={loading || !email || !password}>
              {loading ? <Spinner size="sm" /> : t("auth.login.signIn")}
            </Button>
          </Form>

          <div className="fd-auth-form__links">
            {t("auth.login.noAccount")}{" "}
            <Link to="/auth/register" className="text-primary">
              {t("auth.login.signUp")}
            </Link>
          </div>
        </CardBody>
      </Card>
    </Col>);

}
